import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { api, NO_STATUS, queryString, type SkuListResult, type SkuListRow } from '../api/client.js';
import { DataTable } from '../components/DataTable.js';
import { ProductThumb } from '../components/ProductThumb.js';
import { AnalysisBanner } from '../components/AnalysisBanner.js';
import { SkuStatus } from '../components/SkuStatus.js';
import {
  Alert,
  Button,
  Card,
  Input,
  PageHeader,
  Select,
  Spinner,
  StockNote,
} from '../components/ui.js';
import { day, decimal, integer } from '../format.js';
import {
  compareInteger,
  compareIsoDate,
  compareNumericString,
  compareText,
  sortingFnFor,
} from '../lib/table-sort.js';

/**
 * The SKU master with the sales figures beside each row. The server filters by
 * status and search and hands back every matching SKU at once; the table sorts
 * and pages the whole set on the client, so sorting by Status (or any column)
 * ranks the entire list rather than one server page — which is what made the
 * SKUs and Restock tables disagree on how many SKUs were Active.
 */

const columnHelper = createColumnHelper<SkuListRow>();

// Nullable columns map null to undefined so `sortUndefined: 'last'` keeps them
// last in both directions; every column names an explicit comparator.
const COLUMNS = [
  columnHelper.accessor('sellerSku', {
    header: 'SKU',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => (
      <span className="flex items-center gap-2">
        <ProductThumb asin={c.row.original.asin} title={c.row.original.title} />
        <span className="whitespace-nowrap font-medium text-strong">{c.getValue()}</span>
      </span>
    ),
  }),
  columnHelper.accessor((r) => r.asin ?? undefined, {
    id: 'asin',
    header: 'ASIN',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => c.getValue() ?? '—',
  }),
  columnHelper.accessor((r) => r.title ?? undefined, {
    id: 'title',
    header: 'Title',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => (
      <span className="line-clamp-1 max-w-md" title={c.getValue()}>
        {c.getValue() ?? '—'}
      </span>
    ),
  }),
  columnHelper.accessor((r) => r.status ?? undefined, {
    id: 'status',
    header: 'Status',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => (
      <SkuStatus
        status={c.row.original.status}
        delistedOn={c.row.original.delistedOn}
        listingStatus={c.row.original.listingStatus}
      />
    ),
  }),
  columnHelper.accessor((r) => r.quantity ?? undefined, {
    id: 'quantity',
    header: 'Qty',
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue() ?? null),
  }),
  columnHelper.accessor('units30', {
    header: 'Units 30d',
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('units90', {
    header: 'Units 90d',
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor('units365', {
    header: 'Units 365d',
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  columnHelper.accessor((r) => r.unitsPer30InStock ?? undefined, {
    id: 'unitsPer30InStock',
    header: () => (
      <span title="Units per 30 in-stock days, last stocked 90-day window">U/30 IS</span>
    ),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => decimal(c.getValue() ?? null, 2),
  }),
  columnHelper.accessor((r) => r.sellThroughLastStocked ?? undefined, {
    id: 'sellThroughLastStocked',
    header: () => <span title="Sell-through of the last stocked 90-day window">ST (LS)</span>,
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => decimal(c.getValue() ?? null, 2),
  }),
  columnHelper.accessor((r) => r.lastSaleDate ?? undefined, {
    id: 'lastSaleDate',
    header: 'Last sale',
    sortingFn: sortingFnFor(compareIsoDate),
    cell: (c) => day(c.getValue()),
  }),
];

export function SkusPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);

  const query = useQuery({
    queryKey: ['skus', { status, q }],
    queryFn: () => api.get<SkuListResult>(`/api/skus${queryString({ status, q })}`),
    placeholderData: (previous) => previous,
  });

  const statusCounts = query.data?.statusCounts ?? [];

  function update(next: Record<string, string | undefined>) {
    const merged: Record<string, string> = { status, q };
    for (const [key, value] of Object.entries(next)) {
      merged[key] = value === undefined ? '' : value;
    }
    const cleaned: Record<string, string> = {};
    for (const [key, value] of Object.entries(merged)) {
      if (value) cleaned[key] = value;
    }
    setParams(cleaned);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="SKUs"
        description="Every SKU the reports have mentioned, with what the listing report says about it and how it has sold. Click a row for its history."
      />

      <AnalysisBanner />

      <Card className="p-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            update({ q: search });
          }}
          className="flex flex-wrap items-end gap-3"
        >
          <div className="min-w-64 flex-1">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search SKU, ASIN, or title"
              aria-label="Search"
            />
          </div>
          <div className="w-48">
            <Select
              value={status}
              onChange={(e) => update({ status: e.target.value })}
              aria-label="Status"
            >
              <option value="">Any status</option>
              {statusCounts.map((s) => (
                <option key={s.status} value={s.status}>
                  {s.status === NO_STATUS ? '(no status)' : s.status} ({integer(s.count)})
                </option>
              ))}
            </Select>
          </div>
          <Button type="submit" variant="secondary">
            Search
          </Button>
        </form>
      </Card>

      {query.isPending && <Spinner label="Loading SKUs" />}
      {query.isError && <Alert>{query.error.message}</Alert>}
      {query.data && (
        <>
          {query.data.truncated && (
            <Alert tone="warning">
              Showing the first {integer(query.data.skus.length)} of {integer(query.data.total)}{' '}
              SKUs; sorting and paging cover only the loaded rows. Narrow the search to reach the
              rest.
            </Alert>
          )}
          <Card>
            <DataTable
              data={query.data.skus}
              columns={COLUMNS}
              storageKey="skus"
              resetKey={`${status}|${q}`}
              stickyFirstColumn
              onRowClick={(row) => void navigate(`/skus/${row.id}`)}
              emptyText="No SKUs match. Import an All Listings report to fill the list."
              toolbar={
                <>
                  <span>
                    {query.data.truncated
                      ? `First ${integer(query.data.skus.length)} of ${integer(query.data.total)} SKUs`
                      : `${integer(query.data.total)} SKU${query.data.total === 1 ? '' : 's'}`}
                  </span>
                  <StockNote source={query.data.stock.source} asOf={query.data.stock.asOf} />
                </>
              }
            />
          </Card>
        </>
      )}
    </div>
  );
}
